import moment from "moment";
import { AppointmentsRepository } from "@/repositories/appointments.repository";
import { appointment_status } from "@/models/appointment.model";
import { HomeStats, ChartDataPoint, ChartTimeRange } from "./home.entity";

function getDaysFromTimeRange(timeRange: ChartTimeRange): number {
  return timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
}

function getUpcomingDisplaySuffix(timeRange: ChartTimeRange): string {
  return timeRange === "7d" ? "7 days" : timeRange === "30d" ? "30 days" : "90 days";
}

export const homeApi = {
  async getHomeStats(timeRange: ChartTimeRange): Promise<HomeStats> {
    const days = getDaysFromTimeRange(timeRange);
    const displaySuffix = getUpcomingDisplaySuffix(timeRange);

    // Date range for "last N days" (used for completed count, total count, and go-back rate)
    const startDate = moment().subtract(days, "days").startOf("day").toDate();
    const endDate = moment().endOf("day").toDate();
    const dateFrom = moment(startDate).format("YYYY-MM-DD");
    const dateTo = moment(endDate).format("YYYY-MM-DD");

    const upcomingCount = await AppointmentsRepository.countUpcoming(days);

    // Get completed appointments count in range (both 'done' and 'completed' statuses)
    const [doneResult, completedResult] = await Promise.all([
      AppointmentsRepository.list(1, 1, { status: appointment_status.done, dateFrom, dateTo }, "created_at", "desc"),
      AppointmentsRepository.list(1, 1, { status: appointment_status.completed, dateFrom, dateTo }, "created_at", "desc"),
    ]);
    const completedCount = (doneResult.count || 0) + (completedResult.count || 0);

    // Get total appointments count in range
    const totalResult = await AppointmentsRepository.list(1, 1, { dateFrom, dateTo }, "created_at", "desc");
    const totalCount = totalResult.count || 0;

    // Get goback appointments count in range (using meta_data.goback_at)
    const gobackCount = await AppointmentsRepository.countWithGobackAt(startDate, endDate);

    // Calculate go back rate for the selected range
    const goBackRate = totalCount > 0 ? (gobackCount / totalCount) * 100 : 0;

    return {
      upcomingBookings: {
        count: upcomingCount,
        display: `${upcomingCount} in ${displaySuffix}`,
      },
      completedAppointments: {
        count: completedCount,
        display: completedCount.toString(),
      },
      goBackRate: {
        percentage: goBackRate,
        display: `${goBackRate.toFixed(1)}%`,
      },
    };
  },

  async getChartData(timeRange: ChartTimeRange): Promise<ChartDataPoint[]> {
    const days = getDaysFromTimeRange(timeRange);
    const startDate = moment().subtract(days, 'days').startOf('day').toDate();
    const endDate = moment().endOf('day').toDate();

    // Get appointments in date range
    const appointments = await AppointmentsRepository.listByDateRange(startDate, endDate);

    // Group by date and count
    const dailyCounts: Record<string, number> = {};
    appointments.forEach((apt) => {
      const date = apt.date.split("T")[0]; // Get YYYY-MM-DD
      dailyCounts[date] = (dailyCounts[date] || 0) + 1;
    });

    // Convert to array format and sort by date
    const chartData: ChartDataPoint[] = Object.entries(dailyCounts)
      .map(([date, count]) => ({
        date,
        count,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return chartData;
  },

  async getUpcomingAppointments() {
    const appointments = await AppointmentsRepository.listUpcoming(10);
    return appointments;
  },
};

